/**
 * Wizard de simulation CEE.
 * 3 étapes : (1) Secteur + fiche + date engagement, (2) questionnaire, (3) résultat.
 * Chargement dynamique de la fiche depuis /api/cee/fiches/{code}?engagement_date=...
 * Snapshot version + tarifs à l'évaluation.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRight, Save, Zap, ExternalLink, CheckCircle2, AlertTriangle, XCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';

const SECTEURS = [
  { value: 'residentiel', label: 'Résidentiel', desc: 'Logement individuel ou collectif' },
  { value: 'tertiaire', label: 'Tertiaire', desc: 'Bureaux, commerces, industrie tertiaire' },
];

const AIDES = [
  { value: 'cee', label: 'CEE (Certificats d\'Économies d\'Énergie)',
    desc: 'Prime versée par un délégataire selon les travaux réalisés',
    icon: '⚡', color: 'blue' },
  { value: 'maprimeadapt', label: 'MaPrimeAdapt (adaptation logement)',
    desc: 'Aide Anah pour l\'adaptation du logement aux seniors / PMR',
    icon: '♿', color: 'orange' },
];

export default function CeeSimulationWizard() {
  const { id } = useParams();
  const nav = useNavigate();

  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState([]);
  const [aideType, setAideType] = useState('cee');   // 'cee' | 'maprimeadapt'
  const [secteur, setSecteur] = useState('');
  const [ficheCode, setFicheCode] = useState('');
  const [engagementDate, setEngagementDate] = useState(() =>
    new Date().toISOString().slice(0, 10));
  const [label, setLabel] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [contactId, setContactId] = useState(null);
  const [contactHits, setContactHits] = useState([]);

  const [ficheDef, setFicheDef] = useState(null);
  const [answers, setAnswers] = useState({});
  const [sim, setSim] = useState(null);
  const [result, setResult] = useState(null);
  const [tarifs, setTarifs] = useState([]);
  const [primes, setPrimes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [newBenef, setNewBenef] = useState({ nom: '', email: '', telephone: '', adresse: '', cp_ville: '' });
  const [amoNom, setAmoNom] = useState(''); // MaPrimeAdapt : nom de l'AMO (saisie libre)
  const [convertDelegataireId, setConvertDelegataireId] = useState('');
  const [convertTarifType, setConvertTarifType] = useState('classique');

  // Load catalog
  useEffect(() => {
    api.get('/cee/fiches')
      .then((r) => setCatalog(r.data.fiches || []))
      .catch(() => toast.error('Erreur chargement catalogue CEE'));
  }, []);

  // If ?id → load existing simulation
  useEffect(() => {
    if (!id || id === 'new') return;
    api.get(`/cee/simulations/${id}`)
      .then((r) => {
        setSim(r.data);
        setSecteur(r.data.secteur);
        setFicheCode(r.data.fiche_code);
        setEngagementDate(r.data.engagement_date);
        setLabel(r.data.label || '');
        setContactId(r.data.contact_id || null);
        setAnswers(r.data.answers || {});
        setResult(r.data.result);
        setTarifs(r.data.tarifs_snapshot || []);
        setPrimes(r.data.primes_estimated || []);
        if (r.data.result) setStep(3);
        else if (Object.keys(r.data.answers || {}).length > 0) setStep(2);
      })
      .catch(() => toast.error('Simulation introuvable'));
  }, [id]);

  // Charge la fiche officielle pour la date d'engagement
  useEffect(() => {
    if (!ficheCode || !engagementDate) return;
    api.get(`/cee/fiches/${ficheCode}`, { params: { engagement_date: engagementDate } })
      .then((r) => setFicheDef(r.data))
      .catch(() => setFicheDef(null));
  }, [ficheCode, engagementDate]);

  // Le champ bénéficiaire est désormais libre — aucune recherche CRM ici.
  // La création d'un prospect se fait automatiquement lors de la conversion
  // en dossier (voir openConvertModal / convert).

  const availableFiches = useMemo(() => {
    return (catalog || []).filter((f) => {
      // Filtre par type d'aide
      const type = f.aide_type || 'cee';
      if (aideType && type !== aideType) return false;
      // Filtre par secteur (MPA est toujours résidentiel)
      if (secteur && f.secteur !== secteur) return false;
      return true;
    });
  }, [catalog, secteur, aideType]);

  // Pour MaPrimeAdapt, force résidentiel par défaut
  useEffect(() => {
    if (aideType === 'maprimeadapt' && secteur !== 'residentiel') {
      setSecteur('residentiel');
    }
  }, [aideType, secteur]);

  const saveAndCreate = async () => {
    if (!ficheCode || !engagementDate) {
      toast.error('Sélectionne un secteur, une fiche et une date d\'engagement.');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/cee/simulations', {
        fiche_code: ficheCode,
        engagement_date: engagementDate,
        label: label || undefined,
        contact_id: contactId || undefined,
        answers: {},
      });
      setSim(r.data);
      setStep(2);
      nav(`/cee/simulations/${r.data.simulation_id}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création simulation');
    } finally {
      setSaving(false);
    }
  };

  const saveAnswers = async () => {
    if (!sim) return;
    setSaving(true);
    try {
      const r = await api.put(`/cee/simulations/${sim.simulation_id}`, { answers });
      setSim(r.data);
      toast.success('Brouillon enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  const evaluate = async () => {
    if (!sim) return;
    setSaving(true);
    try {
      await api.put(`/cee/simulations/${sim.simulation_id}`, { answers });
      const r = await api.post(`/cee/simulations/${sim.simulation_id}/evaluate`);
      setSim(r.data);
      setResult(r.data.result);
      setTarifs(r.data.tarifs_snapshot || []);
      setPrimes(r.data.primes_estimated || []);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur évaluation');
    } finally { setSaving(false); }
  };

  const isMpa = sim?.mpa != null;

  const openConvertModal = () => {
    if (!isMpa && !primes.length) {
      toast.error('Aucun tarif délégataire disponible. Configure d\'abord Paramètres → Obligés CEE & Tarifs.');
      return;
    }
    // Pré-sélection : meilleure prime pour le tarif recommandé (CEE uniquement)
    if (!isMpa) {
      const recommandé = sim?.tarif_type_recommande || 'classique';
      setConvertTarifType(recommandé);
      if (primes.length) {
        const best = primes
          .filter((p) => p[`prime_${recommandé}_eur`] != null)
          .sort((a, b) => (b[`prime_${recommandé}_eur`] || 0) - (a[`prime_${recommandé}_eur`] || 0))[0];
        setConvertDelegataireId(best?.delegataire_id || primes[0].delegataire_id);
      }
    }
    setNewBenef({
      nom: contactSearch || '',
      email: '', telephone: '', adresse: '', cp_ville: '',
    });
    setAmoNom('');
    setShowConvertModal(true);
  };

  const convert = async () => {
    if (!sim) return;
    // Sans contact CRM sélectionné → nom du bénéficiaire libre obligatoire
    if (!contactId && !newBenef.nom.trim()) {
      toast.error('Renseigne au moins le nom du bénéficiaire pour créer le dossier.');
      return;
    }
    setSaving(true);
    try {
      const payload = { contact_id: contactId || null };
      if (isMpa) {
        // MaPrimeAdapt : pas de délégataire, mais un AMO libre
        payload.amo_nom = amoNom.trim();
      } else {
        payload.delegataire_id = convertDelegataireId;
        payload.tarif_type = convertTarifType;
      }
      if (!contactId) payload.beneficiaire = { ...newBenef };
      const r = await api.post(`/cee/simulations/${sim.simulation_id}/convert`, payload);
      toast.success('Dossier créé');
      nav(`/cee/dossiers/${r.data.dossier_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur conversion dossier');
    } finally { setSaving(false); setShowConvertModal(false); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="cee-wizard">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Link to="/cee/simulations" className="hover:text-[#1C49A6]">Simulations</Link>
        <span>›</span>
        <span className="text-slate-900 font-medium">
          {sim ? sim.label : 'Nouvelle simulation'}
        </span>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((n) => (
          <div key={n} className="flex-1 flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
              ${step >= n ? 'bg-[#1C49A6] text-white' : 'bg-slate-200 text-slate-500'}`}>
              {n}
            </div>
            <span className={`text-sm font-medium ${step >= n ? 'text-slate-900' : 'text-slate-400'}`}>
              {n === 1 ? 'Choix fiche' : n === 2 ? 'Questionnaire' : 'Résultat'}
            </span>
            {n < 3 && <div className="flex-1 h-px bg-slate-200" />}
          </div>
        ))}
      </div>

      {/* Step 1 : Aide + Secteur + Fiche + Date */}
      {step === 1 && (
        <Card className="p-4 sm:p-6 space-y-6 border-slate-200">
          <div>
            <label className="text-sm font-semibold text-slate-700">Type d'aide</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {AIDES.map((a) => (
                <button
                  key={a.value}
                  data-testid={`cee-aide-${a.value}`}
                  onClick={() => { setAideType(a.value); setFicheCode(''); }}
                  className={`text-left p-4 rounded-lg border-2 transition
                    ${aideType === a.value
                      ? (a.color === 'orange'
                          ? 'border-orange-600 bg-orange-50'
                          : 'border-[#1C49A6] bg-blue-50')
                      : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="text-2xl mb-1">{a.icon}</div>
                  <div className="font-semibold text-slate-900">{a.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{a.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {aideType === 'cee' && (
          <div>
            <label className="text-sm font-semibold text-slate-700">Secteur</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {SECTEURS.map((s) => (
                <button
                  key={s.value}
                  data-testid={`cee-secteur-${s.value}`}
                  onClick={() => { setSecteur(s.value); setFicheCode(''); }}
                  className={`text-left p-4 rounded-lg border-2 transition
                    ${secteur === s.value
                      ? 'border-[#1C49A6] bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="font-semibold text-slate-900">{s.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>
          )}

          {(secteur || aideType === 'maprimeadapt') && (
            <div>
              <label className="text-sm font-semibold text-slate-700">
                {aideType === 'maprimeadapt' ? 'Aide' : 'Fiche CEE'}
              </label>
              <div className="grid gap-3 mt-2">
                {availableFiches.map((f) => (
                  <button
                    key={f.code}
                    data-testid={`cee-fiche-${f.code}`}
                    onClick={() => setFicheCode(f.code)}
                    className={`text-left p-4 rounded-lg border-2 transition
                      ${ficheCode === f.code
                        ? 'border-[#1C49A6] bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-mono text-xs text-slate-500">{f.code} · v{f.version}</div>
                        <div className="font-semibold text-slate-900 mt-1">{f.libelle}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Applicable depuis le {f.applicable_from}
                          {f.arrete ? ` · ${f.arrete}` : ''}
                        </div>
                      </div>
                      <a
                        href={f.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-[#1C49A6] hover:underline flex items-center gap-1 shrink-0"
                      >
                        Source officielle <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {ficheCode && (
            <>
              <div>
                <label className="text-sm font-semibold text-slate-700">Date d'engagement</label>
                <Input
                  type="date"
                  value={engagementDate}
                  onChange={(e) => setEngagementDate(e.target.value)}
                  className="mt-1 w-full sm:max-w-xs"
                  data-testid="cee-engagement-date"
                />
                <div className="text-xs text-slate-500 mt-1">
                  La version de la fiche officielle applicable à cette date sera utilisée.
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Libellé (optionnel)</label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Ex : Maison Dupont — PAC 2026"
                  className="mt-1"
                  data-testid="cee-label"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Nom du bénéficiaire (optionnel)
                </label>
                <Input
                  value={contactSearch}
                  onChange={(e) => { setContactSearch(e.target.value); setContactId(null); setContactHits([]); }}
                  placeholder="Ex : Dupont Jean, Boulangerie Léa SARL…"
                  className="mt-1"
                  data-testid="cee-beneficiaire-libre"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Saisie libre — vous pourrez compléter/associer un contact CRM plus tard,
                  ou créer automatiquement un prospect lors de la conversion en dossier.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => nav('/cee/simulations')}>Annuler</Button>
                <Button
                  onClick={saveAndCreate}
                  disabled={saving}
                  className="bg-[#1C49A6] hover:bg-[#173B85]"
                  data-testid="cee-wizard-next-1"
                >
                  Commencer le questionnaire <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}
        </Card>
      )}

      {/* Step 2 : Questionnaire */}
      {step === 2 && ficheDef && (
        <Card className="p-4 sm:p-6 space-y-6 border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">{ficheDef.libelle}</h2>
              <div className="text-xs text-slate-500 mt-0.5">
                {ficheDef.code} v{ficheDef.version} · Applicable depuis le {ficheDef.applicable_from}
                <a href={ficheDef.source_url} target="_blank" rel="noopener noreferrer"
                  className="ml-2 text-[#1C49A6] hover:underline">
                  Source officielle
                </a>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {ficheDef.questions.map((q) => (
              <QuestionField key={q.id} q={q} answers={answers} setAnswers={setAnswers} />
            ))}
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={saveAnswers} disabled={saving}
                data-testid="cee-save-draft">
                <Save className="w-4 h-4 mr-2" /> Enregistrer brouillon
              </Button>
              <Button onClick={evaluate} disabled={saving}
                className="bg-[#1C49A6] hover:bg-[#173B85]"
                data-testid="cee-evaluate">
                <Zap className="w-4 h-4 mr-2" /> Calculer le résultat
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 3 : Résultat */}
      {step === 3 && result && (
        <>
          <ResultView
            sim={sim} ficheDef={ficheDef} result={result} primes={primes}
            tarifs={tarifs} onBack={() => setStep(2)} onConvert={openConvertModal}
            contactId={contactId} saving={saving}
          />
          {showConvertModal && (
            <ConvertModal
              isMpa={isMpa}
              amoNom={amoNom}
              setAmoNom={setAmoNom}
              mpaAmount={sim?.mpa?.montant_aide_eur}
              mpaCategory={sim?.mpa?.categorie}
              primes={primes}
              tarifType={convertTarifType}
              setTarifType={setConvertTarifType}
              delegataireId={convertDelegataireId}
              setDelegataireId={setConvertDelegataireId}
              recommandé={sim?.tarif_type_recommande}
              hasContact={Boolean(contactId)}
              newBenef={newBenef}
              setNewBenef={setNewBenef}
              onCancel={() => setShowConvertModal(false)}
              onConfirm={convert}
              saving={saving}
            />
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function QuestionField({ q, answers, setAnswers }) {
  const val = answers[q.id] ?? '';
  const set = (v) => setAnswers((a) => ({ ...a, [q.id]: v }));

  return (
    <div>
      <label className="text-sm font-medium text-slate-700">
        {q.label} {q.required && <span className="text-red-500">*</span>}
      </label>
      {q.type === 'text' && (
        <Input value={val} onChange={(e) => set(e.target.value)} className="mt-1"
          data-testid={`cee-q-${q.id}`} />
      )}
      {q.type === 'number' && (
        <Input type="number" value={val}
          min={q.min} max={q.max}
          onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))}
          className="mt-1 w-full sm:max-w-xs" data-testid={`cee-q-${q.id}`} />
      )}
      {q.type === 'date' && (
        <Input type="date" value={val} onChange={(e) => set(e.target.value)}
          className="mt-1 w-full sm:max-w-xs" data-testid={`cee-q-${q.id}`} />
      )}
      {q.type === 'select' && (
        <Select value={val || undefined} onValueChange={(v) => set(v)}>
          <SelectTrigger
            className="mt-1 w-full sm:max-w-md text-sm"
            data-testid={`cee-q-${q.id}`}
          >
            <SelectValue placeholder="— Sélectionner —" />
          </SelectTrigger>
          <SelectContent className="max-h-[70vh]">
            {(q.options || []).map((o) => (
              <SelectItem
                key={o.value}
                value={o.value}
                data-testid={`cee-q-${q.id}-${o.value}`}
              >
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {q.type === 'radio' && (
        <div className="mt-2 flex flex-wrap gap-2">
          {(q.options || []).map((o) => (
            <button
              key={o.value}
              onClick={() => set(o.value)}
              className={`px-3 py-1.5 rounded-md text-sm border transition
                ${val === o.value
                  ? 'border-[#1C49A6] bg-blue-50 text-[#1C49A6] font-semibold'
                  : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
              data-testid={`cee-q-${q.id}-${o.value}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
      {q.help && <div className="text-xs text-slate-500 mt-1">{q.help}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ResultView({ sim, ficheDef, result, primes, tarifs, onBack, onConvert, contactId, saving }) {
  const eligibleIcon = {
    eligible: <CheckCircle2 className="w-8 h-8 text-emerald-600" />,
    non_eligible: <XCircle className="w-8 h-8 text-red-600" />,
    a_verifier: <AlertTriangle className="w-8 h-8 text-amber-500" />,
  };
  const eligibleLabel = {
    eligible: 'Éligible',
    non_eligible: 'Non éligible',
    a_verifier: 'À vérifier',
  };
  const eligibleColor = {
    eligible: 'bg-emerald-50 border-emerald-200',
    non_eligible: 'bg-red-50 border-red-200',
    a_verifier: 'bg-amber-50 border-amber-200',
  };

  return (
    <div className="space-y-6" data-testid="cee-result-view">
      <Card className={`p-6 border-2 ${eligibleColor[result.eligibility]}`}>
        <div className="flex items-start gap-4">
          {eligibleIcon[result.eligibility]}
          <div className="flex-1">
            <div className="text-lg font-bold text-slate-900" data-testid="cee-eligibility-status">
              {eligibleLabel[result.eligibility]}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Fiche {sim?.fiche_code} v{sim?.fiche_version}
              {sim?.fiche_source_url && (
                <a href={sim.fiche_source_url} target="_blank" rel="noopener noreferrer"
                  className="ml-2 text-[#1C49A6] hover:underline">Source officielle</a>
              )}
            </div>
            <div className="mt-4">
              <div className="text-3xl font-bold text-slate-900" data-testid="cee-kwh-cumac">
                {result.kwh_cumac != null
                  ? new Intl.NumberFormat('fr-FR').format(result.kwh_cumac)
                  : '—'}
                <span className="text-sm font-normal text-slate-500 ml-2">kWh cumac</span>
              </div>
              {result.kwh_cumac_status === 'a_verifier' && (
                <Badge className="mt-2 bg-amber-100 text-amber-800 border-0">
                  Calcul à vérifier : voir les points ci-dessous
                </Badge>
              )}
            </div>
          </div>
        </div>
      </Card>

      {result.violations?.length > 0 && (
        <Card className="p-4 border-red-200">
          <div className="font-semibold text-red-800 mb-2">Règles non satisfaites</div>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            {result.violations.map((v, i) => <li key={i}>{v}</li>)}
          </ul>
        </Card>
      )}

      {/* MaPrimeRénov' — bonus additionnel */}
      {sim?.mpr?.applicable && sim.mpr.montant_eur > 0 && (
        <Card className="p-4 border-blue-200 bg-blue-50" data-testid="cee-mpr-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-blue-900">MaPrimeRénov' (bonus)</div>
              <div className="text-xs text-slate-600">
                Catégorie ANAH <b>{sim.mpr.categorie?.toUpperCase()}</b> — bonus cumulable avec la prime CEE
              </div>
            </div>
            <div className="text-2xl font-bold text-blue-800 font-mono">
              +{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(sim.mpr.montant_eur)}
            </div>
          </div>
        </Card>
      )}
      {sim?.mpr?.categorie === 'rose' && (
        <Card className="p-3 border-slate-200 text-xs text-slate-600" data-testid="cee-mpr-rose">
          MaPrimeRénov' : catégorie ROSE — revenus supérieurs, non éligible.
        </Card>
      )}

      {/* MaPrimeAdapt — module autonomie */}
      {sim?.mpa?.applicable && (
        <Card className="p-4 border-orange-200 bg-orange-50" data-testid="cee-mpa-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-orange-900">MaPrimeAdapt</div>
              <div className="text-xs text-slate-600">
                Catégorie ANAH <b>{sim.mpa.categorie?.toUpperCase()}</b> — taux {Math.round((sim.mpa.taux || 0) * 100)} %
                {sim.mpa.montant_travaux_ht_plafonne
                  ? ` sur ${new Intl.NumberFormat('fr-FR').format(sim.mpa.montant_travaux_ht_plafonne)} € HT`
                  : ''}
              </div>
            </div>
            <div className="text-2xl font-bold text-orange-800 font-mono">
              {sim.mpa.montant_aide_eur != null
                ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(sim.mpa.montant_aide_eur)
                : '—'}
            </div>
          </div>
        </Card>
      )}

      {result.determinants?.length > 0 && (
        <Card className="p-4 border-slate-200">
          <div className="font-semibold text-slate-900 mb-2">Réponses déterminantes</div>
          <div className="grid sm:grid-cols-2 gap-2 text-sm">
            {result.determinants.map((d, i) => (
              <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                <span className="text-slate-600">{d.question}</span>
                <span className="font-medium text-slate-900">{d.value}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Primes estimées */}
      {sim?.mpa == null && (
      <Card className="border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="font-semibold text-slate-900">Primes estimées par délégataire</div>
          <div className="text-xs text-slate-500">
            {tarifs.length === 0 ? 'Aucun tarif configuré — saisir dans Paramètres → CEE.' : `${tarifs.length} tarif(s) actif(s)`}
          </div>
        </div>
        {primes.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">
            Aucun tarif délégataire actif pour cette fiche. Configure les tarifs dans <Link to="/cee/tarifs" className="text-[#1C49A6] hover:underline">Paramètres CEE</Link>.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-slate-600 text-xs uppercase">Délégataire</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-600 text-xs uppercase">€/MWh Classique</th>
                <th className="text-right px-4 py-2 font-semibold text-orange-700 text-xs uppercase">€/MWh Précarité</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-600 text-xs uppercase">Prime Classique</th>
                <th className="text-right px-4 py-2 font-semibold text-orange-700 text-xs uppercase">Prime Précarité</th>
                <th className="text-left px-4 py-2 font-semibold text-slate-600 text-xs uppercase">Conditions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {primes.map((p, i) => {
                const recommandé = sim?.tarif_type_recommande;
                return (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-900 font-medium">{p.delegataire_nom}</td>
                    <td className="px-4 py-2 text-right font-mono">{p.eur_per_mwh_cumac || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2 text-right font-mono">{p.eur_per_mwh_cumac_precarite || <span className="text-slate-300">—</span>}</td>
                    <td className={`px-4 py-2 text-right font-mono ${recommandé === 'classique' ? 'font-bold bg-emerald-50 text-emerald-900' : ''}`}>
                      {p.prime_classique_eur != null
                        ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p.prime_classique_eur)
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className={`px-4 py-2 text-right font-mono ${recommandé === 'precarite' ? 'font-bold bg-orange-50 text-orange-900' : ''}`}>
                      {p.prime_precarite_eur != null
                        ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p.prime_precarite_eur)
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-600 text-xs">{p.conditions || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </Card>
      )}

      {sim?.tarif_type_recommande && (
        <div className={`text-xs px-3 py-2 rounded ${sim.tarif_type_recommande === 'precarite'
          ? 'bg-orange-50 border border-orange-200 text-orange-900'
          : 'bg-emerald-50 border border-emerald-200 text-emerald-900'}`}>
          Détection automatique du profil : <b>{sim.tarif_type_recommande === 'precarite' ? 'Coup de pouce précarité' : 'Barème classique'}</b>
          {sim.tarif_type_recommande === 'precarite' && ' — la prime précarité sera pré-sélectionnée à la conversion.'}
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Modifier les réponses
        </Button>
        <Button
          onClick={onConvert}
          disabled={saving || result.eligibility === 'non_eligible'}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="cee-convert-to-dossier"
        >
          <FileText className="w-4 h-4 mr-2" /> Convertir en dossier CEE
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ConvertModal({ isMpa, amoNom, setAmoNom, mpaAmount, mpaCategory,
                          primes, tarifType, setTarifType, delegataireId, setDelegataireId, recommandé,
                          hasContact, newBenef, setNewBenef, onCancel, onConfirm, saving }) {
  const options = primes || [];
  const selected = options.find((p) => p.delegataire_id === delegataireId);
  const primeKey = `prime_${tarifType}_eur`;
  const primeValue = selected?.[primeKey];

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
      data-testid="cee-convert-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-slate-900">
          {isMpa ? 'Convertir en dossier MaPrimeAdapt' : 'Convertir en dossier CEE'}
        </h3>
        <p className="text-sm text-slate-600">
          {isMpa
            ? "MaPrimeAdapt est financée par l'Anah — pas de délégataire CEE. Indique le nom de l'AMO en charge du dossier."
            : "Choisis le délégataire avec lequel démarrer ce dossier. Le tarif et la prime en euros seront figés au moment de la conversion."}
        </p>

        {!hasContact && (
          <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/60 space-y-3"
                data-testid="cee-convert-new-prospect">
            <div>
              <div className="text-sm font-semibold text-blue-900 mb-1">
                Nouveau bénéficiaire (aucun contact CRM sélectionné)
              </div>
              <div className="text-[11px] text-slate-600">
                Un prospect sera créé automatiquement dans « Contacts » avec les infos ci-dessous.
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input type="text" placeholder="Nom / Raison sociale *"
                value={newBenef.nom}
                onChange={(e) => setNewBenef({ ...newBenef, nom: e.target.value })}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="cee-convert-nom" />
              <input type="tel" placeholder="Téléphone"
                value={newBenef.telephone}
                onChange={(e) => setNewBenef({ ...newBenef, telephone: e.target.value })}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="cee-convert-tel" />
              <input type="email" placeholder="Email"
                value={newBenef.email}
                onChange={(e) => setNewBenef({ ...newBenef, email: e.target.value })}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm sm:col-span-2"
                data-testid="cee-convert-email" />
              <input type="text" placeholder="Adresse chantier"
                value={newBenef.adresse}
                onChange={(e) => setNewBenef({ ...newBenef, adresse: e.target.value })}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="cee-convert-adresse" />
              <input type="text" placeholder="Code postal + Ville"
                value={newBenef.cp_ville}
                onChange={(e) => setNewBenef({ ...newBenef, cp_ville: e.target.value })}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="cee-convert-cpville" />
            </div>
          </div>
        )}

        {isMpa ? (
          <>
            <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/60">
              <label className="text-sm font-semibold text-orange-900 block mb-2">
                Nom de l'AMO (Assistant à Maîtrise d'Ouvrage) *
              </label>
              <input
                type="text"
                value={amoNom}
                onChange={(e) => setAmoNom(e.target.value)}
                placeholder="Nom de l'AMO ou de la personne en charge du dossier"
                className="w-full border-2 border-orange-300 rounded-md px-4 py-3 text-base font-semibold focus:outline-none focus:border-orange-600"
                data-testid="cee-convert-amo-nom"
              />
              <p className="text-[11px] text-orange-800 mt-2">
                L'AMO accompagne le bénéficiaire tout au long du dossier MaPrimeAdapt (diagnostic, montage, dépôt Anah).
              </p>
            </div>
            <div className="bg-slate-50 rounded p-3">
              <div className="text-xs text-slate-500">Aide MaPrimeAdapt estimée</div>
              <div className="text-2xl font-bold text-orange-700 font-mono">
                {mpaAmount != null
                  ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(mpaAmount)
                  : <span className="text-amber-600 text-base">non calculable</span>}
              </div>
              {mpaCategory && (
                <div className="text-xs text-slate-500 mt-1">
                  Catégorie ANAH <b>{mpaCategory.toUpperCase()}</b>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="text-sm font-semibold text-slate-700">Type de tarif</label>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setTarifType('classique')}
                  className={`flex-1 px-3 py-2 rounded border-2 text-sm transition
                    ${tarifType === 'classique'
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-900 font-semibold'
                      : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                  data-testid="cee-convert-type-classique"
                >
                  Classique{recommandé === 'classique' && ' ★ recommandé'}
                </button>
                <button
                  onClick={() => setTarifType('precarite')}
                  className={`flex-1 px-3 py-2 rounded border-2 text-sm transition
                    ${tarifType === 'precarite'
                      ? 'border-orange-600 bg-orange-50 text-orange-900 font-semibold'
                      : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                  data-testid="cee-convert-type-precarite"
                >
                  Précarité{recommandé === 'precarite' && ' ★ recommandé'}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Délégataire & valorisation</label>
              <div className="mt-2 grid gap-2 max-h-64 overflow-y-auto">
                {options.map((p) => {
                  const prime = p[`prime_${tarifType}_eur`];
                  const eurMwh = tarifType === 'precarite'
                    ? p.eur_per_mwh_cumac_precarite : p.eur_per_mwh_cumac;
                  const isSelected = p.delegataire_id === delegataireId;
                  const disabled = prime == null;
                  return (
                    <button
                      key={p.delegataire_id}
                      type="button"
                      disabled={disabled}
                      onClick={() => setDelegataireId(p.delegataire_id)}
                      data-testid={`cee-convert-deleg-${p.delegataire_id}`}
                      className={`text-left rounded-lg border-2 p-3 transition
                        ${isSelected ? 'border-[#1C49A6] bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'}
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">
                            {p.delegataire_nom}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Valo {tarifType === 'precarite' ? 'précarité' : 'classique'} :{' '}
                            <span className="font-mono">
                              {eurMwh != null
                                ? `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(eurMwh)} €/MWh`
                                : '—'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`font-bold text-lg font-mono ${
                            disabled ? 'text-slate-400' : 'text-emerald-700'}`}>
                            {prime != null
                              ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(prime)
                              : 'tarif absent'}
                          </div>
                          {isSelected && (
                            <div className="text-[10px] font-semibold text-[#1C49A6]">
                              ✓ Sélectionné
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="bg-slate-50 rounded p-3">
              <div className="text-xs text-slate-500">Prime figée à la conversion</div>
              <div className="text-2xl font-bold text-slate-900">
                {primeValue != null
                  ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(primeValue)
                  : <span className="text-amber-600 text-base">non disponible</span>}
              </div>
              {selected && (
                <div className="text-xs text-slate-500 mt-1">
                  {selected.delegataire_nom} · {tarifType}
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded text-slate-700 hover:bg-slate-100 text-sm"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={
              saving ||
              (!hasContact && !newBenef.nom.trim()) ||
              (!isMpa && (!delegataireId || primeValue == null))
            }
            className={`px-4 py-2 rounded text-white text-sm font-semibold disabled:opacity-50 ${
              isMpa ? 'bg-orange-600 hover:bg-orange-700' : 'bg-[#1C49A6] hover:bg-[#173B85]'
            }`}
            data-testid="cee-convert-confirm"
          >
            {saving ? 'Création…' : 'Créer le dossier'}
          </button>
        </div>
      </div>
    </div>
  );
}
